import React, { ReactNode, useEffect, useState } from 'react'

import "../styles/documentViewer.scss";
import { DocumentService } from '../services/documentService';
import { useUserContext } from '../contexts/userContext';
import { AppConfig } from '../config/appConfig';
import moment from 'moment';
import { Alert, Button } from '@mui/material';
import Spinner from './spinner';
import { Buffer } from 'buffer';
import { Icons } from './icons';
import { IDocument } from '../types/document';

interface IViewerDocument {
	uri: string;
}

export interface IDocumentViewerProps {
	documentId: number | undefined,
	showBackButton?: boolean;
	viewInline?: boolean
	onCloseRequest?: () => void;
}

const DocumentViewer: React.FunctionComponent<IDocumentViewerProps> = ({ documentId, showBackButton = false, onCloseRequest }) => {
	const userContext = useUserContext();

	//For viewing documents
	const [isLoadingDocument, setIsLoadingDocument] = useState(true);
	const [documentResponse, setDocumentResponse] = useState<IDocument | undefined>(undefined);
	const [previewUrl, setPreviewUrl] = useState("");
	const [downloadUrl, setDownloadUrl] = useState("");
	const [fileName, setFileName] = useState("");
	const [title, setTitle] = useState("");
	const [documentError, setDocumentError] = useState<string | ReactNode | undefined>(undefined);
	const iframeRef = React.useRef<HTMLIFrameElement>(null);

	useEffect(() => {
		loadDocument();
	}, [documentId])

	useEffect(() => {
		if (documentResponse) {
			viewDocument();
		}
	}, [documentResponse])

	const handleLoad = () => {
		try {
			const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;

			const style = iframeDoc.createElement('style');
			style.textContent = `
				html {
					height: auto !important;

					body {
						height: auto !important;
					}

					img {
						width: -webkit-fill-available;
						height: 100%;
					}				
				}
			`;

			iframeDoc.head.appendChild(style);
		} catch (error) {
			console.error("Cross-origin restriction blocked styling:", error);
		}
	};

	const loadDocument = () => {
		if (documentId) {
			DocumentService.getDocument(documentId)
				.then((response) => {
					console.log('DocumentService.getDocument: SUCCESS', response);
					setDocumentResponse(response);
				})
				.catch((error) => {
					console.log('DocumentService.getDocument: ERROR', error);
					setDocumentResponse(undefined);
					setTitle("Document Not Found");
				})
				.finally(() => {
					setIsLoadingDocument(false);
				});
		}
	}

	const getFileExtension = (fileName: string): string => {
		const parts = fileName.split('.');
		if (parts.length > 1) {
			return parts[parts.length - 1].toLowerCase();
		}
		return "";
	}

	const viewDocument = () => {
		if (documentResponse.content) {
			let newTitle = documentResponse.entityName ? documentResponse.entityName + " " : "";
			newTitle = newTitle + documentResponse.documentTypeName;
			if (documentResponse.expirationDate) {
				newTitle = newTitle + " - Expires " + moment(documentResponse.expirationDate).format("MM/DD/YYYY");
			}

			setTitle(newTitle);
			if (documentResponse.name) setFileName(documentResponse.name);
			const blob = new Blob([Buffer.from(documentResponse.content, "base64")], {
				type: documentResponse.contentType
			});
			const blobUrl = URL.createObjectURL(blob);
			setDownloadUrl(blobUrl);

			if (AppConfig.viewableDocumentTypes.filter((item) => { return item == documentResponse.contentType?.toLowerCase() ? true : false; }).length > 0) {
				setPreviewUrl(blobUrl);
				setDocumentError(undefined);
			}
			else {
				setPreviewUrl(undefined);
				const fileExtension = getFileExtension(documentResponse.name ?? "").toUpperCase() || "(nothing)";
				setDocumentError(<>Documents ending in <strong>{fileExtension}</strong> <i>({documentResponse.contentType})</i> are not supported for viewing.</>);
			}
		}
	}
	return (
		<div className="hwi-document-viewer">
			<section className="search-bar">
				<div className="search-header">
					<div className="title">
						{showBackButton &&
							<span onClick={() => onCloseRequest?.()}>
								<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
							</span>
						}
						{title}
					</div>
					<div className="controls">
						{!isLoadingDocument && documentResponse &&
							<>
								<a href={downloadUrl} target="_blank"><Button variant='outlined'>Open In New Tab</Button></a>
								<a href={downloadUrl} download={fileName}><Button variant='outlined'>Download</Button></a>
							</>
						}
					</div>
				</div>
			</section>
			<Spinner dependency={isLoadingDocument} text='Retrieving Document...'>
				{documentResponse ? (
					<div className="document-wrapper">
						<Alert className="alert" severity="info">
							Depending on the document type and your browser settings you may not be able to preview documents in the application. If your document does not load below, please use the Download button to download a local copy.
						</Alert>
						{documentResponse.isSensitive &&
							<Alert className="alert" severity="warning">
								This document contains sensitive information and should not be shared with unauthorized individuals.
							</Alert>
						}
						{previewUrl &&
							<iframe className="document-frame" ref={iframeRef} onLoad={handleLoad} src={previewUrl}></iframe>
						}
						{documentError &&
							<div className="view-error">{documentError}</div>
						}
					</div>
				) : (
					<div className="document-wrapper">
						<Alert className="alert" severity="error">
							The requested document could not be found or you do not have permission to view it.
						</Alert>
					</div>
				)}

			</Spinner>
		</div>
	)
}

export default DocumentViewer;